import { useEffect, useMemo, useState } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import OrderForm from './OrderForm';
import {CancelOrderModal} from './CancelOrderModal';
import {ValidateOrderModal} from './ValidateOrderModal';
import DeliveryNoteForm from './DeliveryNoteForm'; // ← Import du modal BL
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';

/* ================= STATUS ================= */
const STATUS_LABELS: Record<string, string> = {
  BROUILLON: 'En attente',
  VALIDE: 'Confirmée',
  EN_COURS: 'En cours',
  LIVRE_PARTIEL: 'Livrée partiellement',
  LIVRE: 'Livrée',
  ANNULE: 'Annulée',
};

const STATUS_COLORS: Record<string, string> = {
  BROUILLON: 'bg-yellow-100 text-yellow-800',
  VALIDE: 'bg-blue-100 text-blue-800',
  EN_COURS: 'bg-purple-100 text-purple-800',
  LIVRE_PARTIEL: 'bg-green-100 text-green-800',
  LIVRE: 'bg-green-100 text-green-800',
  ANNULE: 'bg-red-100 text-red-800',
};

export default function OrdersList() {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [showForm, setShowForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState<any>(null);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);

  const [showValidateModal, setShowValidateModal] = useState(false);
  const [orderToValidate, setOrderToValidate] = useState<any>(null);

  // Nouveaux états pour le modal BL
  const [showDeliveryNoteModal, setShowDeliveryNoteModal] = useState(false);
  const [selectedOrderForBL, setSelectedOrderForBL] = useState<any>(null);

  const navigate = useNavigate();

  /* ================= FETCH ================= */
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await getResources('bons-commande-clients');
      setOrders(res.data.data ?? []);
    } catch {
      toast.error('Erreur chargement des BCC');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  /* ================= FILTER ================= */
  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      const matchesSearch =
        o.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        o.client?.raison_sociale?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === 'all' || o.statut === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [orders, searchTerm, statusFilter]);

  /* ================= STATS ================= */
  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.statut === 'BROUILLON').length,
    progress: orders.filter(o => o.statut === 'EN_COURS').length,
    totalAmount: orders.reduce((s, o) => s + Number(o.total_ttc || 0), 0),
  };

  /* ================= ACTIONS ================= */
  const handleEdit = (order: any) => {
    setEditingOrder(order);
    setShowForm(true);
  };

  const handleValidate = async (order: any) => {
    try {
      await createResource(`bons-commande-clients/${order.id}/valider`,{});
      toast.success(`Commande ${order.numero} validée`);
      fetchOrders();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Validation impossible');
    }
  };

  const handleCancel = async (order: any) => {
    try {
      await createResource(`bons-commande-clients/${order.id}/annuler`,{});
      toast.success(`Commande ${order.numero} annulée`);
      fetchOrders();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Annulation impossible');
    }
  };

  // Ouvre le modal BL
  const handleCreateBL = (order: any) => {
    setSelectedOrderForBL(order);
    setShowDeliveryNoteModal(true);
  };

  const handlePrintBCC = (bcc: any) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Impossible d'ouvrir la fenêtre d'impression");
      return;
    }

    const totalTTC = Number(bcc.total_ttc);
    const totalHT = bcc.total_ht ?? totalTTC / 1.1925;
    const tvaMontant = bcc.total_tva ?? totalTTC - totalHT;
    const remiseGlobale = Number(bcc.remise_globale ?? 0);
    const sousTotalHT = bcc.lignes.reduce(
      (sum, l) => sum + Number(l.total_ht || 0),
      0
    );

    const html = `<!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="utf-8">
      <title>BCC ${bcc.numero}</title>
      <style>
        body { font-family: 'Segoe UI', Arial, sans-serif; margin: 40px; background: #f9fafb; color: #333; }
        .container { max-width: 900px; margin: 0 auto; background: white; padding: 40px; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
        .header { text-align: center; padding-bottom: 20px; border-bottom: 3px solid #2563eb; }
        .header h1 { color: #1e40af; margin: 0; font-size: 28px; }
        .header h2 { color: #1e3a8a; margin: 10px 0 0; font-size: 22px; }

        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin: 30px 0; }
        .info-block h3 { color: #1e40af; border-bottom: 1px solid #dbeafe; padding-bottom: 8px; margin-bottom: 12px; }

        table { width: 100%; border-collapse: collapse; margin: 30px 0; }
        th, td { border: 1px solid #e5e7eb; padding: 12px; }
        th { background: #eff6ff; color: #1e40af; }

        .text-right { text-align: right; }
        .text-center { text-align: center; }

        .totals { float: right; width: 400px; margin-top: 30px; }
        .total-line { display: flex; justify-content: space-between; padding: 8px 0; border-top: 1px solid #e5e7eb; }
        .total-final { font-size: 18px; font-weight: bold; color: #1e40af; border-top: 2px solid #2563eb; padding-top: 12px; }

        .notes { margin-top: 50px; padding: 20px; background: #f3f4f6; border-radius: 8px; }

        .footer { margin-top: 60px; text-align: center; color: #6b7280; font-size: 12px; }

        @media print {
          body { background: white; margin: 0; }
          .container { box-shadow: none; padding: 20px; }
        }
      </style>
    </head>
    <body>
      <div class="container">

        <div class="header">
          <h1>BON DE COMMANDE CLIENT</h1>
          <h2>${bcc.numero}</h2>
        </div>

        <div class="info-grid">
          <div class="info-block">
            <h3>Client</h3>
            <div><strong>Raison sociale :</strong> ${bcc.client.raison_sociale}</div>
            <div><strong>Code :</strong> ${bcc.client.code}</div>
            <div><strong>Téléphone :</strong> ${bcc.client.telephone || 'Non renseigné'}</div>
            <div><strong>Email :</strong> ${bcc.client.email || 'Non renseigné'}</div>
          </div>

          <div class="info-block">
            <h3>Informations commande</h3>
            <div><strong>Date commande :</strong> ${new Date(bcc.date_commande).toLocaleDateString('fr-FR')}</div>
            <div><strong>Livraison souhaitée :</strong> ${bcc.date_livraison_souhaitee ? new Date(bcc.date_livraison_souhaitee).toLocaleDateString('fr-FR') : '—'}</div>
            <div><strong>Commercial :</strong> ${bcc.commercial?.name || 'Non assigné'}</div>
            <div><strong>Statut :</strong> ${bcc.statut}</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Référence</th>
              <th>Désignation</th>
              <th class="text-center">Quantité</th>
              <th class="text-right">Prix U. HT</th>
              <th class="text-right">Remise</th>
              <th class="text-right">Total HT</th>
            </tr>
          </thead>
          <tbody>
            ${bcc.lignes.map((l: any) => {
              const remise = l.remise_ligne
                ? `${Number(l.remise_ligne).toFixed(2)}%`
                : '-';

              return `
                <tr>
                  <td>${l.produit?.reference || '—'}</td>
                  <td>${l.designation}</td>
                  <td class="text-center">${l.quantite_commandee}</td>
                  <td class="text-right">${Number(l.prix_unitaire_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</td>
                  <td class="text-right">${remise}</td>
                  <td class="text-right">${Number(l.total_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>

        <div class="totals">
          <div class="total-line"><span>Sous-total HT :</span><span>${sousTotalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</span></div>
          ${remiseGlobale > 0 ? `
          <div class="total-line" style="color:#dc2626;"><span>Remise globale :</span>
            <span>- ${remiseGlobale.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</span>
          </div>
        ` : ''}
          <div class="total-line">
            <span>Total HT :</span>
            <span>${totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</span>
          </div>
          <div class="total-line">
            <span>TVA (19.25%) :</span>
            <span>${tvaMontant.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</span>
          </div>
          <div class="total-line total-final">
            <span>Total TTC :</span>
            <span>${totalTTC.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</span>
          </div>
        </div>
        <div style="clear:both;"></div>

        ${bcc.notes ? `
          <div class="notes">
            <strong>Notes :</strong><br>
            ${bcc.notes.replace(/\n/g, '<br>')}
          </div>
        ` : ''}

        <div class="footer">
          Document généré le ${new Date().toLocaleDateString('fr-FR')}
          à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </div>

      </div>
    </body>
    </html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 800);
  };

  const handleBackToList = () => {
    setEditingOrder(null);
    setShowForm(false);
  };

  if (showForm) {
    return <OrderForm order={editingOrder} onBack={handleBackToList} />;
  }

  /* ================= RENDER ================= */
  return (
    <div className="space-y-6">
      <Toaster position="top-right" />

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div className="flex gap-4">
          <input
            placeholder="Rechercher numéro ou client…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="px-3 py-2 border rounded-md"
          />

          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded-md"
          >
            <option value="all">Tous les statuts</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat title="Commandes" value={stats.total} icon="ri-shopping-cart-line" />
        <Stat title="En attente" value={stats.pending} icon="ri-time-line" />
        <Stat title="En cours" value={stats.progress} icon="ri-truck-line" />
        <Stat title="CA Total" value={`${stats.totalAmount.toLocaleString()} €`} icon="ri-money-euro-circle-line" />
      </div>

      {/* TABLE */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y">
            <thead className="bg-gray-50">
              <tr>
                <Th>N° BCC</Th>
                <Th>Client</Th>
                <Th>Date</Th>
                <Th>Livraison</Th>
                <Th>Montant</Th>
                <Th>Statut</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredOrders.map(o => (
                <tr key={o.id} className="hover:bg-gray-50">
                  <Td className="font-medium">{o.numero}</Td>

                  <Td>
                    <div className="font-medium">{o.client?.raison_sociale}</div>
                    <div className="text-xs text-gray-500">{o.client?.email}</div>
                  </Td>

                  <Td>{new Date(o.date_commande).toLocaleDateString('fr-FR')}</Td>

                  <Td>
                    {o.date_livraison_souhaitee
                      ? new Date(o.date_livraison_souhaitee).toLocaleDateString('fr-FR')
                      : '-'}
                  </Td>

                  <Td className="font-medium">
                    {Number(o.total_ttc).toLocaleString()} €
                  </Td>

                  <Td>
                    <span className={`px-2 py-1 text-xs rounded-full ${STATUS_COLORS[o.statut]}`}>
                      {STATUS_LABELS[o.statut]}
                    </span>
                  </Td>

                  <Td>
                    <div className="flex gap-3">
                      <IconBtn
                        title="Détail"
                        icon="ri-eye-line"
                        className="text-indigo-600"
                        onClick={() => navigate(`/bcc/${o.id}`)}
                      />
                      {o.statut === 'BROUILLON' && (
                        <>
                          <IconBtn icon="ri-edit-line" title="Modifier" className="text-blue-600" onClick={() => handleEdit(o)} />
                          <IconBtn icon="ri-check-line" title="Valider" className="text-green-600" onClick={() => {setOrderToValidate(o);setShowValidateModal(true);}} />
                          <IconBtn icon="ri-close-circle-line" title="Annuler" className="text-red-600" onClick={() => handleCancel(o)} />
                        </>
                      )}

                      {(o.statut === 'VALIDE' || o.statut === 'EN_COURS') && (
                        <>
                          <IconBtn 
                            icon="ri-truck-line" 
                            title="Créer BL" 
                            className="text-green-600" 
                            onClick={() => handleCreateBL(o)} 
                          />
                          <IconBtn icon="ri-close-circle-line" title="Annuler" className="text-red-600" onClick={() => {setSelectedOrder(o);setShowCancelModal(true);}} />
                        </>
                      )}

                      {(o.statut === 'LIVRE_PARTIEL') && (
                        <>
                          <IconBtn 
                            icon="ri-truck-line" 
                            title="Créer BL" 
                            className="text-green-600" 
                            onClick={() => handleCreateBL(o)} 
                          />
                        </>
                      )}

                      {o.statut !== 'ANNULE' && (
                        <IconBtn icon="ri-printer-line" title="Imprimer" className="text-gray-600" onClick={() => handlePrintBCC(o)} />
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modals existants */}
        <CancelOrderModal
          open={showCancelModal}
          order={selectedOrder}
          onClose={() => {
            setShowCancelModal(false);
            setSelectedOrder(null);
          }}
          onConfirm={async (order) => {
            await handleCancel(order);
            setShowCancelModal(false);
            setSelectedOrder(null);
          }}
        />
        <ValidateOrderModal
          open={showValidateModal}
          order={orderToValidate}
          onClose={() => {
            setShowValidateModal(false);
            setOrderToValidate(null);
          }}
          onConfirm={async (order) => {
            await handleValidate(order);
            setShowValidateModal(false);
            setOrderToValidate(null);
          }}
        />

        {/* Nouveau modal Bon de Livraison */}
        {showDeliveryNoteModal && selectedOrderForBL && (
          <DeliveryNoteForm
            order={selectedOrderForBL}
            onClose={() => {
              setShowDeliveryNoteModal(false);
              setSelectedOrderForBL(null);
            }}
            onSuccess={() => {
              toast.success('Bon de Livraison créé avec succès');
              fetchOrders(); // Rafraîchir la liste pour mettre à jour les quantités livrées
            }}
          />
        )}

        {!loading && filteredOrders.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Aucune commande trouvée
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= UI ================= */
function Stat({ title, value, icon }: any) {
  return (
    <div className="bg-white p-4 rounded-lg shadow flex items-center">
      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
        <i className={`${icon} text-blue-600`} />
      </div>
      <div>
        <div className="text-sm text-gray-500">{title}</div>
        <div className="text-lg font-semibold">{value}</div>
      </div>
    </div>
  );
}

function Th({ children }: any) {
  return <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{children}</th>;
}

function Td({ children, className = '' }: any) {
  return <td className={`px-6 py-4 text-sm ${className}`}>{children}</td>;
}

function IconBtn({ icon, title, onClick, className = '' }: any) {
  return (
    <button onClick={onClick} title={title} className={`hover:opacity-80 ${className}`}>
      <i className={icon} />
    </button>
  );
}